import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Form, Select, Space, Input, Popconfirm, Flex } from 'antd';
import { useAtomValue, useSetAtom } from 'jotai';
import { DateTime } from 'luxon';
import { focusResaAtom } from '../store/core';
import { isInOpenHours, locationAtom, ResaLocation } from '../store/location';
import { allReservationsAtom, mergeReservations } from '../store/reservation';
import { DatePicker } from './AntLuxon';
import { LocationSelect } from './LocationSelect';
import { ParticipantsSelect } from './ParticipantsSelect';
import { ResaCalendarEvent } from './ResaCalendar';
import { FormattedMessage, useIntl } from 'react-intl';
import { allUsersAtom } from '../store/user';

export const EditEvent = (props: {
	event: ResaCalendarEvent;
	close: () => void;
	commit: (e: ResaCalendarEvent) => void;
}) => {
	const [proxyEvent, setProxyEvent] = useState<ResaCalendarEvent>(props.event);
	// useEffect(() => setProxyEvent(props.event), [props.event]);

	const focusResa = useAtomValue(focusResaAtom);
	const setReservations = useSetAtom(allReservationsAtom);

	const onConfirm = useCallback(() => {
		// TODO, if location is not current location warn and ask if going to the new location
		props.commit(proxyEvent);
	}, [props.commit, proxyEvent]);

	const cancelEvent = useCallback(async () => {
		if (!proxyEvent) return;
		const realId = parseInt(proxyEvent.resource.id); // real events have real resource ids, with numeric ids
		if (isNaN(realId)) {
			throw new Error('Unexpected: cannot cancel a new or anonymized event');
		}
		const rollback = proxyEvent;
		setReservations((all) => all.filter((r) => r.id != proxyEvent.resource.id));
		try {
			await focusResa.cancelReservation({ resaId: realId });
			props.close();
		} catch (e) {
			console.error(e, e.issues);
			setReservations((all) => mergeReservations(all, [rollback.resource]));
		}
	}, [focusResa, proxyEvent]);

	const acceptInvite = useCallback(async () => {
		if (!proxyEvent) return;
		const realId = parseInt(proxyEvent.resource.id); // real events have real resource ids, with numeric ids
		if (isNaN(realId)) return;
		await focusResa.acceptInvite({ resaId: realId });
		setReservations((all) =>
			all.map((r) =>
				r.id != proxyEvent.resource.id
					? r
					: {
							...r,
							users: r.users.map((u) =>
								u.userId == focusResa.loggedInUser
									? { ...u, accepted: true }
									: u,
							),
						},
			),
		);
		setProxyEvent((e) => ({
			...e,
			resource: {
				...e.resource,
				users: e.resource.users.map((u) =>
					u.userId == focusResa.loggedInUser ? { ...u, accepted: true } : u,
				),
			},
		}));
	}, [focusResa, proxyEvent]);

	const approve = useCallback(async () => {
		if (!proxyEvent) return;
		const realId = parseInt(proxyEvent.resource.id); // real events have real resource ids, with numeric ids
		if (isNaN(realId)) return;
		await focusResa.approveReservation({ resaId: realId });
		setReservations((all) =>
			all.map((r) =>
				r.id != proxyEvent.resource.id
					? r
					: {
							...r,
							approved: new Date(),
						},
			),
		);
		setProxyEvent((e) => ({
			...e,
			resource: {
				...e.resource,
				approved: new Date(),
			},
		}));
	}, [focusResa, proxyEvent]);

	const location = useAtomValue(
		locationAtom(proxyEvent.resource.locationId ?? 0),
	);

	const [usersToCleanse, setUsersToCleanse] = useState<number[]>([]);
	const allUsers = useAtomValue(allUsersAtom);
	useEffect(() => {
		const i = setTimeout(() => {
			setUsersToCleanse(() => {
				return proxyEvent.resource.users.filter((u) => 
					!allUsers.some((user) => user.ID == u.userId)
				).map((u) => u.userId);
			});
		}, 5000)
		return () => clearTimeout(i);
	}, [allUsers, proxyEvent.resource.users])
	const cleanse = useCallback(() => {
		setReservations((all) => all.map((r) =>
			r.id != proxyEvent.resource.id ? r : {
				...r,
				users: r.users.filter((u) => !usersToCleanse.includes(u.userId)),
			}
		));
		setProxyEvent((e) => ({
			...e,
			resource: {
				...e.resource,
				users: e.resource.users.filter((u) => !usersToCleanse.includes(u.userId)),
			},
		}));
		setUsersToCleanse([]);
	}, [proxyEvent.resource.id, usersToCleanse])

	const isNew = proxyEvent.id == 'new';
	const needsApproval = location?.requiresApproval;
	const isApproved =
		proxyEvent.resource.anonymized || !!proxyEvent.resource.approved;
	const isAdmin = focusResa.isAdmin;
	const isOwn = proxyEvent.resource.users.some(
		(u) => u.userId == focusResa.loggedInUser,
	);
	const isAccepted = proxyEvent.resource.users.some(
		(u) => u.userId == focusResa.loggedInUser && u.accepted,
	);
	const invalid =
		location?.openHours != null
			? !isInOpenHours(props.event, location.openHours)
			: false;
	const awaitingParticipants =
		props.event.resource.users.length == 0 ||
		props.event.resource.users.some((u) => !u.accepted);
	const intl = useIntl();

	const eventIsValid = proxyEvent.resource.users.length > 0;

	return (
		<>
			<div className="modal-header">
				{isNew ? (
					<FormattedMessage
						defaultMessage="Créer une réservation"
						id="CCjw3Z"
					/>
				) : (
					<FormattedMessage
						defaultMessage="Editer la réservation"
						id="czPPw6"
					/>
				)}
			</div>
			<Form labelCol={{ span: 5 }}>
				<Form.Item
					label={
						<FormattedMessage
							defaultMessage="Horaires"
							id="jnguH8"
							description="Formulaire d'edition d'evenement"
						/>
					}
				>
					<CustomStartEndPicker
						start={proxyEvent.start}
						end={proxyEvent.end}
						location={location}
						onChange={({ start, end }: { start: Date; end: Date }) => {
							setProxyEvent((e) => ({
								...e,
								start: start,
								end: end,
								resource: {
									...e.resource,
									start: start,
									end: end,
								},
							}));
						}}
					/>
				</Form.Item>
				<Form.Item
					label={
						<FormattedMessage
							defaultMessage="Lieu"
							id="aGi5DY"
							description="Formulaire d'edition d'evenement"
						/>
					}
				>
					<Flex align="baseline" gap="1em">
						<LocationSelect
							locationId={proxyEvent.resource.locationId}
							onChange={(locationId) =>
								setProxyEvent((e) => ({
									...e,
									resource: {
										...e.resource,
										locationId,
									},
								}))
							}
						/>
						{location && location.ID > -1 && (
							<a href={location.url} target="_blank" rel="noreferrer">
								<FormattedMessage defaultMessage="infos" id="SqnvnX" />
							</a>
						)}
					</Flex>
				</Form.Item>
				<Form.Item
					label={
						<FormattedMessage
							defaultMessage="Participants"
							id="cwWTom"
							description="Formulaire d'edition d'evenement"
						/>
					}
					validateStatus={
						proxyEvent.resource.users.length == 0 ? 'error' : undefined
					}
				>
					<ParticipantsSelect
						users={proxyEvent.resource.users}
						onChange={(users) =>
							setProxyEvent((e) => ({
								...e,
								resource: {
									...e.resource,
									users: [
										...users,
										// cannot unselect an already accepted participant
										...e.resource.users.filter(
											(uu) =>
												uu.accepted &&
												!users.some((uuu) => uuu.userId === uu.userId),
										),
									],
								},
							}))
						}
					/>
					{isAdmin && (
						<FormattedMessage
							defaultMessage="(Admin) note: le premier participant de la liste sera considéré comme la personne ayant créé la réservation et ne recevra pas d'invitation"
							id="qcqy9+"
						/>
					)}
					{usersToCleanse.length > 0 && (
						<>
							<FormattedMessage
								defaultMessage="Cet évènement référence des participants qui ne sont plus inscrits."
								id="4Ecbvz"
							/>
							<Button danger onClick={cleanse}>
								<FormattedMessage
									id="GSUwqA"
									defaultMessage="Supprimer les participants non inscrits"
								/>
							</Button>
						</>
					)}
				</Form.Item>
				{!proxyEvent.resource.anonymized && (
					<Form.Item
						label={
							<FormattedMessage
								defaultMessage="Notes"
								id="CmeKLl"
								description="Formulaire d'édition d'évènement, champs notes"
							/>
						}
					>
						<Input.TextArea
							defaultValue={proxyEvent.resource.notes ?? ''}
							maxLength={focusResa.notesMaxLength}
							showCount
							autoSize={true}
							placeholder={intl.formatMessage({
								id: 'g1Tofw',
								defaultMessage:
									"Les notes seront partagées avec tous les participants ci-dessus.\nPréférez l'anglais afin que tout le monde soit inclus.",
								description:
									"Formulaire d'édition d'évènement, placeholder du champs notes",
							})}
							onChange={(changeEvent) =>
								setProxyEvent((e) => ({
									...e,
									resource: {
										...e.resource,
										notes: changeEvent.target.value,
									},
								}))
							}
						/>
					</Form.Item>
				)}
			</Form>
			<Space direction="vertical" style={{ width: '100%' }}>
				{!isNew && (isAdmin || isAccepted) && (
					<Space
						size="large"
						align="center"
						direction="vertical"
						style={{ width: '100%', margin: '2em 0' }}
					>
						<Popconfirm
							title={
								<FormattedMessage
									defaultMessage="Êtes-vous sûr ?"
									id="1aU1rB"
								/>
							}
							okText={
								<FormattedMessage
									defaultMessage="Oui, Annuler la réservation"
									id="0m+4A6"
								/>
							}
							okButtonProps={{
								danger: true,
								size: 'middle',
							}}
							cancelText={<FormattedMessage defaultMessage="Non" id="Baow22" />}
							cancelButtonProps={{
								size: 'middle',
							}}
							onConfirm={cancelEvent}
							arrow={false}
						>
							<Button type="primary" danger>
								<FormattedMessage
									defaultMessage="Annuler la réservation"
									id="7vKxwY"
								/>
							</Button>
						</Popconfirm>
					</Space>
				)}
				{isOwn && !isAccepted && (
					<Space>
						<Button type="primary" onClick={acceptInvite}>
							<FormattedMessage defaultMessage="Accepter" id="56kk/w" />
						</Button>
					</Space>
				)}
				<Space>
					{(isAdmin || isAccepted) && (
						<Button type="primary" onClick={onConfirm} disabled={!eventIsValid}>
							{isNew ? (
								needsApproval ? (
									<FormattedMessage
										defaultMessage="Demander la réservation"
										id="XCxStq"
									/>
								) : (
									<FormattedMessage
										defaultMessage="Créer la réservation"
										id="Mmg1Nd"
									/>
								)
							) : (
								<FormattedMessage defaultMessage="Modifier" id="ho0AzM" />
							)}
						</Button>
					)}
					{isAdmin && needsApproval && !isApproved && (
						<Button
							type="primary"
							disabled={invalid || awaitingParticipants}
							onClick={approve}
						>
							<FormattedMessage defaultMessage="Approuver" id="xDBpDz" />
						</Button>
					)}
					<Space direction="vertical" size={0}>
						{!isAdmin && needsApproval && (
							<FormattedMessage
								defaultMessage="Ce lieu requiert une approbation du staff pour toute réservation"
								id="gomomu"
							/>
						)}
						{!isAdmin && (
							<FormattedMessage
								defaultMessage="Pensez aux temps de trajet."
								id="T5Tujl"
							/>
						)}
					</Space>
				</Space>
				{isAdmin && (
					<div
						style={{ fontSize: '10px', lineHeight: '10px', margin: '5px 0' }}
					>
						{JSON.stringify(proxyEvent.resource)}
					</div>
				)}
			</Space>
		</>
	);
};

const CustomStartEndPicker = (props: {
	start: Date;
	end: Date;
	location?: ResaLocation;
	onChange: (o: { start: Date; end: Date }) => void;
}) => {
	const focusResa = useAtomValue(focusResaAtom);

	const days = useMemo(() => {
		const day1 = DateTime.fromJSDate(focusResa.dates.start).startOf('day');
		const days = [];
		for (
			let d = day1;
			d.toMillis() <= focusResa.dates.end.getTime();
			d = d.plus({ days: 1 })
		) {
			days.push(DateTime.fromMillis(d.toMillis()));
		}
		return days;
	}, [focusResa]);

	const [start] = useState<DateTime>(DateTime.fromJSDate(props.start));
	const [end] = useState<DateTime>(DateTime.fromJSDate(props.end));

	const openHours = useMemo(
		() =>
			props.location?.openHours?.find((o) =>
				isInOpenHours(start.toJSDate(), [o]),
			),
		[props.location],
	);

	const disabledTime = useMemo(() => {
		return () => ({
			disabledHours: () =>
				[
					0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19,
					20, 21, 22, 23,
				].filter(
					(h) =>
						h < (openHours?.start.getHours() ?? 0) ||
						h > (openHours?.end.getHours() ?? 23),
				),
		});
	}, [openHours]);

	const setStartEnd = useCallback(
		(newStart?: DateTime, newEnd?: DateTime) => {
			const s = (newStart ?? start).toJSDate();
			const e = (newEnd ?? end).toJSDate();
			props.onChange({ start: s < e ? s : e, end: s < e ? e : s });
		},
		[props.onChange, start, end],
	);

	return (
		<div className="datetime-selector">
			<Select
				size="middle"
				className="date-selector"
				defaultValue={start.startOf('day').toMillis()}
				options={days.map((d) => {
					const l = d.toLocaleString({
						weekday: 'long',
						day: 'numeric',
					});
					return {
						value: d.toMillis(),
						label: l,
					};
				})}
				onChange={(value) => {
					const n = DateTime.fromMillis(value);
					setStartEnd(
						DateTime.fromObject({
							year: n.year,
							month: n.month,
							day: n.day,
							hour: start.hour,
							minute: start.minute,
						}),
						DateTime.fromObject({
							year: n.year,
							month: n.month,
							day: n.day,
							hour: end.hour,
							minute: end.minute,
						}),
					);
				}}
			/>
			<DatePicker.RangePicker
				size="small"
				picker="time"
				mode={['time', 'time']}
				minDate={
					openHours
						? DateTime.fromJSDate(openHours.start)
						: start.startOf('day')
				}
				maxDate={
					openHours ? DateTime.fromJSDate(openHours.end) : end.endOf('day')
				}
				needConfirm={false}
				minuteStep={15}
				defaultValue={[start, end]}
				format={'HH:mm'}
				disabledTime={disabledTime}
				hideDisabledOptions={true}
				onChange={(d) => {
					if (!d) return;
					const [s, e] = d;
					setStartEnd(s ?? undefined, e ?? undefined);
				}}
			/>
		</div>
	);
};
